import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add JWT token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If token expired and we have a refresh token, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { token } = response.data;

          // Save new token
          localStorage.setItem('token', token);

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============ Authentication ============

export const registerUser = async (email, password, fullName, company = '') => {
  try {
    const response = await api.post('/auth/register', {
      email,
      password,
      fullName,
      company,
    });
    return response.data;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const response = await api.post('/auth/login', {
      email,
      password,
    });
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    await api.post('/auth/logout');
  } catch (error) {
    console.error('Error logging out:', error);
  } finally {
    // Always clear tokens, even if logout request fails
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }
};

export const refreshAccessToken = async (refreshToken) => {
  try {
    const response = await api.post('/auth/refresh', { refreshToken });
    return response.data;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

// ============ User Management ============

// User management
export const createUserSession = async () => {
  try {
    const response = await api.post('/users');
    return response.data;
  } catch (error) {
    console.error('Error creating user session:', error);
    throw error;
  }
};

// Valuation
export const submitValuation = async (userId, inputData) => {
  try {
    const response = await api.post('/valuate', {
      userId,
      inputData,
    });
    return response.data;
  } catch (error) {
    console.error('Error submitting valuation:', error);
    throw error;
  }
};

export const getValuation = async (valuationId) => {
  try {
    const response = await api.get(`/valuations/${valuationId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching valuation:', error);
    throw error;
  }
};

export const updateValuation = async (valuationId, inputData) => {
  try {
    const response = await api.put(`/valuations/${valuationId}`, {
      inputData,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating valuation:', error);
    throw error;
  }
};

export const markImprovementAsCompleted = async (valuationId, improvementKey) => {
  try {
    const response = await api.post(
      `/valuations/${valuationId}/improvements/${improvementKey}`
    );
    return response.data;
  } catch (error) {
    console.error('Error marking improvement as completed:', error);
    throw error;
  }
};

export const getUserValuations = async (userId) => {
  try {
    const response = await api.get(`/users/${userId}/valuations`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user valuations:', error);
    throw error;
  }
};

export const checkHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error checking API health:', error);
    throw error;
  }
};

// ============ VAC (Value Acceleration Calculator) ============

export const calculateVAC = async (vacInput) => {
  try {
    const response = await api.post('/vac/calculate', vacInput);
    return response.data;
  } catch (error) {
    console.error('Error calculating VAC:', error);
    throw error;
  }
};

export const calculateRiskScore = async (riskAnswers) => {
  try {
    const response = await api.post('/vac/risk-score', { riskAnswers });
    return response.data;
  } catch (error) {
    console.error('Error calculating risk score:', error);
    throw error;
  }
};

export const getIndustryMultiples = async () => {
  try {
    const response = await api.get('/vac/industry-multiples');
    return response.data;
  } catch (error) {
    console.error('Error fetching industry multiples:', error);
    throw error;
  }
};

export const getIndustryMultiple = async (naicsCode) => {
  try {
    const response = await api.get(`/vac/industry-multiples/${naicsCode}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching industry multiple:', error);
    throw error;
  }
};

export const getVACSettings = async () => {
  try {
    const response = await api.get('/vac/settings');
    return response.data;
  } catch (error) {
    console.error('Error fetching VAC settings:', error);
    throw error;
  }
};

export const getVACResult = async (valuationId) => {
  try {
    const response = await api.get(`/vac/results/${valuationId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching VAC result:', error);
    throw error;
  }
};

// ============ Questionnaire ============

export const getQuestionnaireTemplates = async () => {
  try {
    const response = await api.get('/questionnaires/templates');
    return response.data;
  } catch (error) {
    console.error('Error fetching questionnaire templates:', error);
    throw error;
  }
};

export const getQuestionnaireTemplate = async (templateId) => {
  try {
    const response = await api.get(`/questionnaires/templates/${templateId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching questionnaire template:', error);
    throw error;
  }
};

export const getDefaultQuestionnaire = async () => {
  try {
    const response = await api.get('/questionnaires/default');
    return response.data;
  } catch (error) {
    console.error('Error fetching default questionnaire:', error);
    throw error;
  }
};

export const saveQuestionnaireResponses = async (valuationId, responses) => {
  try {
    const response = await api.post('/questionnaires/responses', {
      valuationId,
      responses
    });
    return response.data;
  } catch (error) {
    console.error('Error saving questionnaire responses:', error);
    throw error;
  }
};

export const getQuestionnaireResponses = async (valuationId) => {
  try {
    const response = await api.get(`/questionnaires/responses/${valuationId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching questionnaire responses:', error);
    throw error;
  }
};

export const convertResponsesToVAC = async (valuationId) => {
  try {
    const response = await api.post(`/questionnaires/responses/${valuationId}/convert-to-vac`);
    return response.data;
  } catch (error) {
    console.error('Error converting responses to VAC:', error);
    throw error;
  }
};

// ============ MVA (Market Value Assessment) ============

export const calculateMVA = async (mvaInput) => {
  try {
    const response = await api.post('/mva/calculate', mvaInput);
    return response.data;
  } catch (error) {
    console.error('Error calculating MVA:', error);
    throw error;
  }
};

export const getMVAResult = async (valuationId) => {
  try {
    const response = await api.get(`/mva/${valuationId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching MVA result:', error);
    throw error;
  }
};

export const getUserMVAResults = async (userId) => {
  try {
    const response = await api.get(`/mva/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user MVA results:', error);
    throw error;
  }
};

export const getBuyerAnalysis = async (vacResult, businessProfile = {}) => {
  try {
    const response = await api.post('/mva/buyer-analysis', {
      vacResult,
      businessProfile
    });
    return response.data;
  } catch (error) {
    console.error('Error getting buyer analysis:', error);
    throw error;
  }
};

export const compareDealStructures = async (params) => {
  try {
    const response = await api.post('/mva/deal-structure', params);
    return response.data;
  } catch (error) {
    console.error('Error comparing deal structures:', error);
    throw error;
  }
};

export const calculateAssetSaleTax = async (params) => {
  try {
    const response = await api.post('/mva/tax/asset-sale', params);
    return response.data;
  } catch (error) {
    console.error('Error calculating asset sale tax:', error);
    throw error;
  }
};

export const calculateStockSaleTax = async (params) => {
  try {
    const response = await api.post('/mva/tax/stock-sale', params);
    return response.data;
  } catch (error) {
    console.error('Error calculating stock sale tax:', error);
    throw error;
  }
};

export const calculateEarnoutScenarios = async (params) => {
  try {
    const response = await api.post('/mva/earnout', params);
    return response.data;
  } catch (error) {
    console.error('Error calculating earnout scenarios:', error);
    throw error;
  }
};

export const calculateSellerFinancing = async (params) => {
  try {
    const response = await api.post('/mva/seller-financing', params);
    return response.data;
  } catch (error) {
    console.error('Error calculating seller financing:', error);
    throw error;
  }
};

export const calculateWorkingCapitalAdjustment = async (params) => {
  try {
    const response = await api.post('/mva/working-capital', params);
    return response.data;
  } catch (error) {
    console.error('Error calculating working capital adjustment:', error);
    throw error;
  }
};

export const calculateNetProceeds = async (params) => {
  try {
    const response = await api.post('/mva/net-proceeds', params);
    return response.data;
  } catch (error) {
    console.error('Error calculating net proceeds:', error);
    throw error;
  }
};

export const getTaxRates = async () => {
  try {
    const response = await api.get('/mva/reference/tax-rates');
    return response.data;
  } catch (error) {
    console.error('Error fetching tax rates:', error);
    throw error;
  }
};

export const getBuyerProfiles = async () => {
  try {
    const response = await api.get('/mva/reference/buyer-profiles');
    return response.data;
  } catch (error) {
    console.error('Error fetching buyer profiles:', error);
    throw error;
  }
};

export const getDealStructures = async () => {
  try {
    const response = await api.get('/mva/reference/deal-structures');
    return response.data;
  } catch (error) {
    console.error('Error fetching deal structures:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Lite funnel (Plan 05) — public valuation submit / claim / verify, authed fetch
// ---------------------------------------------------------------------------

export const submitLiteValuation = async (mvaInput) => {
  const response = await api.post('/lite/valuations', mvaInput);
  return response.data; // { valuationId }
};

export const claimLiteValuation = async (valuationId, email) => {
  const response = await api.post(`/lite/valuations/${valuationId}/claim`, { email });
  return response.data; // { message, devLink? }
};

export const verifyMagicLink = async (valuationId, token) => {
  const response = await api.post('/lite/verify', { valuationId, token });
  return response.data; // { token, refreshToken, expiresIn, user, valuationId }
};

export const getLiteValuation = async (valuationId) => {
  const response = await api.get(`/lite/valuations/${valuationId}`);
  return response.data; // { valuationId, status, createdAt, input, result }
};

export const downloadLiteValuationPdf = async (valuationId) => {
  const response = await api.post(`/lite/valuations/${valuationId}/export-pdf`, {}, {
    responseType: 'blob',
  });
  return response.data; // Blob
};

// ---------------------------------------------------------------------------
// Conversational intake (Plan: 2026-08-10-conversational-intake)
// ---------------------------------------------------------------------------

export const getIntakeConfig = async () => {
  const response = await api.get('/lite/intake/config');
  return response.data; // { enabled }
};

export const createIntake = async () => {
  const response = await api.post('/lite/intake');
  return response.data; // { intakeId, greeting }
};

export const sendIntakeMessage = async (id, message) => {
  const response = await api.post(`/lite/intake/${id}/messages`, { message });
  return response.data; // { assistantMessage, progress: {requiredMissing, riskAnswered, ready}, readyToConfirm, capped }
};

export const getIntake = async (id) => {
  const response = await api.get(`/lite/intake/${id}`);
  return response.data; // { status, transcript, partialInput, progress, riskNotes }
};

export const completeIntake = async (id) => {
  const response = await api.post(`/lite/intake/${id}/complete`);
  return response.data; // { mvaInput }
};

export const confirmIntake = async (id, confirmedPaths = []) => {
  const response = await api.post(`/lite/intake/${id}/confirmed`, { confirmedPaths });
  return response.data; // { status }
};
